import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLOURS = {
  teal: '#00D4C8',
  gold: '#C9A84C',
  violet: '#7C5CFC',
  gray: '#888780',
};

const SLIDER_CSS = `
input[type=range].sw-slider{-webkit-appearance:none;appearance:none;width:100%;height:3px;border-radius:2px;background:rgba(255,255,255,0.12);outline:none;cursor:pointer}
input[type=range].slider-bot::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;background:#7C5CFC;cursor:pointer;border:2px solid #0a0a0a;box-shadow:0 0 0 3px rgba(124,92,252,0.2)}
input[type=range].slider-borrow::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;background:#C9A84C;cursor:pointer;border:2px solid #0a0a0a;box-shadow:0 0 0 3px rgba(201,168,76,0.2)}
input[type=range].slider-build::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;background:#00D4C8;cursor:pointer;border:2px solid #0a0a0a;box-shadow:0 0 0 3px rgba(0,212,200,0.2)}
input[type=range].slider-buy::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;background:#888780;cursor:pointer;border:2px solid #0a0a0a;box-shadow:0 0 0 3px rgba(136,135,128,0.2)}
@keyframes tooltipPulse{0%,100%{box-shadow:0 0 0 0 rgba(201,168,76,0.5)}50%{box-shadow:0 0 0 5px rgba(201,168,76,0)}}
`;

const InfoTooltip = ({ text }) => {
  const [visible, setVisible] = React.useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 5, verticalAlign: 'middle' }}>
      <span
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onClick={() => setVisible(!visible)}
        style={{
          width: 14, height: 14, borderRadius: '50%',
          background: 'rgba(201,168,76,0.15)', border: '1px solid #C9A84C',
          color: '#C9A84C', fontSize: 9, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', animation: 'tooltipPulse 2s infinite',
        }}
      >i</span>
      {visible && (
        <span style={{
          position: 'absolute', bottom: '120%', left: '50%',
          transform: 'translateX(-50%)', background: '#1a1a1a',
          color: '#ffffff', fontSize: 11, lineHeight: 1.5,
          padding: '6px 10px', borderRadius: 6, whiteSpace: 'normal',
          width: 'max-content', minWidth: 200, maxWidth: 260,
          zIndex: 100, textTransform: 'none', fontFamily: 'inherit',
          letterSpacing: 'normal',
          border: '1px solid rgba(255,255,255,0.12)', pointerEvents: 'none',
        }}>{text}</span>
      )}
    </span>
  );
};

const DEFAULT_SCENARIOS = [
  { index: 0, name: 'Automate First',    bot_pct: 40, borrow_pct: 20, build_pct: 25, buy_pct: 15 },
  { index: 1, name: 'Hybrid Transition', bot_pct: 20, borrow_pct: 30, build_pct: 35, buy_pct: 15 },
  { index: 2, name: 'Talent-Led',        bot_pct: 10, borrow_pct: 15, build_pct: 50, buy_pct: 25 },
];

const formatCurrency = (value, currency) => {
  if (value == null || isNaN(value)) return '-';
  const sign = value < 0 ? '-' : '';
  const v = Math.abs(value);
  switch (currency) {
    case 'INR': return `${sign}₹${(v / 10000000).toFixed(1)}Cr`;
    case 'USD': return `${sign}$${(v / 1000000).toFixed(1)}M`;
    case 'AED': return `${sign}AED ${(v / 1000000).toFixed(1)}M`;
    case 'GBP': return `${sign}£${(v / 1000000).toFixed(1)}M`;
    default: return String(value);
  }
};

const computeOutputs = (scenario, labourCost) => {
  const bot = scenario.bot_pct || 0;
  const borrow = scenario.borrow_pct || 0;
  const build = scenario.build_pct || 0;
  const buy = scenario.buy_pct || 0;
  const botSaving = labourCost * (bot / 100) * 0.70;
  const borrowSaving = labourCost * (borrow / 100) * 0.25;
  const buildCost = labourCost * (build / 100) * 0.10;
  const buyCost = labourCost * (buy / 100) * 0.15;
  const netDelta = botSaving + borrowSaving - buildCost - buyCost;
  const timeline = bot > 30 ? 18 : build > 40 ? 12 : 24;
  const risk = bot > 35 ? 'high' : bot > 20 ? 'medium' : 'low';
  return { netDelta, timeline, risk };
};

const SLIDER_CLASSES = { bot_pct: 'slider-bot', borrow_pct: 'slider-borrow', build_pct: 'slider-build', buy_pct: 'slider-buy' };
const D = { card: '#141414', border: 'rgba(255,255,255,0.08)', text1: '#ffffff', text2: 'rgba(255,255,255,0.5)' };

const ScenarioWorkshop = ({ orgFunctions = [], currency = 'INR', initialFunction = null }) => {
  const isDemo = typeof window !== 'undefined' && localStorage.getItem('is_demo') === 'true';
  const [scenarioDocs, setScenarioDocs] = useState({});
  const [activeFunctionId, setActiveFunctionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [guidance, setGuidance] = useState(null);
  const [guidanceLoading, setGuidanceLoading] = useState(false);
  const debouncerRef = useRef({});

  useEffect(() => {
    if (!orgFunctions.length) return;
    if (initialFunction) {
      const match = orgFunctions.find((f) => f.name === initialFunction);
      if (match) { setActiveFunctionId(match.function_id); return; }
    }
    setActiveFunctionId(orgFunctions[0].function_id);
  }, [orgFunctions, initialFunction]);

  useEffect(() => {
    if (!orgFunctions.length) { setLoading(false); return; }
    if (isDemo) {
      const docs = {};
      orgFunctions.forEach((fn) => {
        docs[fn.function_id] = {
          id: `demo-${fn.function_id}`, function_id: fn.function_id, function_name: fn.name,
          headcount: fn.headcount || 0, labour_cost: fn.labour_cost || 0, currency,
          committed: false, committed_scenario_index: null,
          scenarios: DEFAULT_SCENARIOS.map((s) => ({ ...s })),
        };
      });
      setScenarioDocs(docs); setLoading(false); return;
    }
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      const next = {};
      for (const fn of orgFunctions) {
        try {
          const res = await axios.get(`${API}/chro/scenarios`, { headers, params: { function_id: fn.function_id } });
          next[fn.function_id] = res.data;
        } catch (err) {
          if (err?.response?.status === 404) {
            try {
              const created = await axios.post(`${API}/chro/scenarios`, {
                function_id: fn.function_id, function_name: fn.name,
                headcount: fn.headcount || 0, labour_cost: fn.labour_cost || 0, currency,
              }, { headers });
              next[fn.function_id] = created.data;
            } catch (e) { /* skip */ }
          }
        }
      }
      setScenarioDocs(next); setLoading(false);
    })();
  }, [orgFunctions, currency, isDemo]);

  const activeFn = orgFunctions.find((f) => f.function_id === activeFunctionId);
  const activeDoc = activeFn ? scenarioDocs[activeFn.function_id] : null;

  const debouncedSave = (functionId, scenarios) => {
    if (isDemo) return;
    if (debouncerRef.current[functionId]) clearTimeout(debouncerRef.current[functionId]);
    debouncerRef.current[functionId] = setTimeout(async () => {
      const doc = scenarioDocs[functionId];
      if (!doc) return;
      const token = localStorage.getItem('token');
      try {
        setSaving(true);
        await axios.put(`${API}/chro/scenarios/${doc.id}`, { scenarios }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) { /* ignore */ }
      finally { setSaving(false); }
    }, 800);
  };

  const updateSlider = (scenarioIndex, key, value) => {
    if (!activeDoc) return;
    const newScenarios = activeDoc.scenarios.map((s, i) => i === scenarioIndex ? { ...s, [key]: value } : s);
    setScenarioDocs((prev) => ({ ...prev, [activeFunctionId]: { ...activeDoc, scenarios: newScenarios } }));
    debouncedSave(activeFunctionId, newScenarios);
  };

  const updateName = (scenarioIndex, name) => {
    if (!activeDoc) return;
    const newScenarios = activeDoc.scenarios.map((s, i) => i === scenarioIndex ? { ...s, name } : s);
    setScenarioDocs((prev) => ({ ...prev, [activeFunctionId]: { ...activeDoc, scenarios: newScenarios } }));
    debouncedSave(activeFunctionId, newScenarios);
  };

  const handleCommit = async () => {
    if (isDemo) { alert('Full access required to commit scenarios'); return; }
    if (!activeDoc) return;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const labourCost = activeDoc.labour_cost || 0;
    let bestIdx = 0, bestDelta = -Infinity;
    activeDoc.scenarios.forEach((s, i) => {
      const { netDelta } = computeOutputs(s, labourCost);
      if (netDelta > bestDelta) { bestDelta = netDelta; bestIdx = i; }
    });
    try {
      setSaving(true);
      await axios.put(`${API}/chro/scenarios/${activeDoc.id}`, { scenarios: activeDoc.scenarios }, { headers });
      const res = await axios.post(`${API}/chro/scenarios/${activeDoc.id}/commit`, { scenario_index: bestIdx }, { headers });
      setScenarioDocs((prev) => ({ ...prev, [activeFunctionId]: res.data }));
    } catch (e) { alert('Failed to commit scenario'); }
    finally { setSaving(false); }
  };

  const handleGetGuidance = async () => {
    if (!activeDoc || !activeFn) return;
    setGuidanceLoading(true);
    setGuidance(null);
    const lc = activeDoc.labour_cost || 0;
    const scenarioPayload = (activeDoc.scenarios || []).map((s) => {
      const o = computeOutputs(s, lc);
      return {
        name: s.name,
        bot_pct: s.bot_pct, borrow_pct: s.borrow_pct,
        build_pct: s.build_pct, buy_pct: s.buy_pct,
        cost_delta: formatCurrency(o.netDelta, currency),
        timeline: `${o.timeline} months`,
        risk: o.risk,
      };
    });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/chro/scenario-guidance`, {
        function_name: activeFn.name,
        currency,
        scenarios: scenarioPayload,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setGuidance(res.data.guidance);
    } catch (e) {
      setGuidance('Unable to generate guidance at this time. Please try again.');
    } finally {
      setGuidanceLoading(false);
    }
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: D.text2 }}>Loading scenarios…</div>;
  if (!orgFunctions.length) return <div style={{ padding: 48, textAlign: 'center', color: D.text2 }}>No functions available.</div>;
  if (!activeDoc || !activeFn) return <div style={{ padding: 48, textAlign: 'center', color: D.text2 }}>Select a function.</div>;

  const labourCost = activeDoc.labour_cost || 0;
  const scenarios = activeDoc.scenarios || [];
  const allOutputs = scenarios.map((s) => computeOutputs(s, labourCost));
  const maxAbsDelta = Math.max(...allOutputs.map((o) => Math.abs(o.netDelta)), 1);
  const scenarioColours = [COLOURS.violet, COLOURS.gold, COLOURS.teal];

  return (
    <div style={{ padding: '16px 4px' }}>
      <style>{`
        @keyframes tooltipPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(201,168,76,0.5); }
          50% { box-shadow: 0 0 0 5px rgba(201,168,76,0); }
        }
      `}</style>
      <style dangerouslySetInnerHTML={{ __html: SLIDER_CSS }} />

      {/* SECTION 1 - function pills */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 14 }}>
        {orgFunctions.map((fn) => {
          const active = fn.function_id === activeFunctionId;
          return (
            <button key={fn.function_id} onClick={() => setActiveFunctionId(fn.function_id)}
              style={{
                padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 500,
                whiteSpace: 'nowrap', cursor: 'pointer', height: 32,
                background: active ? COLOURS.teal : 'transparent',
                color: active ? '#fff' : D.text2,
                border: active ? `1px solid ${COLOURS.teal}` : `1px solid ${D.border}`,
              }}>
              {fn.name}
            </button>
          );
        })}
      </div>

      {/* SECTION 2 - meta cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
        <div style={{ background: D.card, borderRadius: 12, border: `1px solid ${D.border}`, padding: '10px 14px' }}>
          <div style={{ fontSize: 12, color: D.text2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Function <InfoTooltip text="The function you are modelling. Switch between functions using the pills above." />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4, color: D.text1 }}>{activeFn.name}</div>
          <div style={{ fontSize: 11, color: D.text2, marginTop: 2 }}>{activeFn.headcount || 0} people</div>
        </div>
        <div style={{ background: D.card, borderRadius: 12, border: `1px solid ${D.border}`, padding: '10px 14px' }}>
          <div style={{ fontSize: 12, color: D.text2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Labour cost <InfoTooltip text="Estimated annual spend on people in this function, used to compute the financial impact of each scenario." />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4, color: COLOURS.teal }}>{formatCurrency(labourCost, currency)}</div>
        </div>
        <div style={{ background: D.card, borderRadius: 12, border: `1px solid ${D.border}`, padding: '10px 14px' }}>
          <div style={{ fontSize: 12, color: D.text2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Scenarios <InfoTooltip text="Compare up to three transformation strategies side by side. Adjust the sliders to model your preferred approach." />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4, color: D.text1 }}>3</div>
          <div style={{ fontSize: 11, color: D.text2, marginTop: 2 }}>Adjust to compare</div>
        </div>
      </div>

      {/* SECTION 3 - three scenario cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
        {scenarios.map((s, i) => {
          const total = (s.bot_pct || 0) + (s.borrow_pct || 0) + (s.build_pct || 0) + (s.buy_pct || 0);
          const { netDelta, timeline, risk } = computeOutputs(s, labourCost);
          const isCommitted = activeDoc.committed && activeDoc.committed_scenario_index === i;
          const riskMap = {
            high:   { bg: 'rgba(220,38,38,0.15)', text: '#dc2626', label: 'High readiness risk' },
            medium: { bg: 'rgba(217,119,6,0.15)', text: '#d97706', label: 'Medium risk' },
            low:    { bg: 'rgba(22,163,74,0.15)', text: '#16a34a', label: 'Low risk' },
          };
          const r = riskMap[risk];

          return (
            <div key={i} style={{
              background: D.card, borderRadius: 12,
              border: isCommitted ? `2px solid ${COLOURS.teal}` : `1px solid ${D.border}`,
              padding: 14, display: 'flex', flexDirection: 'column', gap: 8,
              overflow: 'visible', minHeight: 380,
            }}>
              <input type="text" value={s.name}
                onChange={(e) => updateName(i, e.target.value)}
                onFocus={(e) => (e.target.style.borderBottomColor = COLOURS.teal)}
                onBlur={(e) => (e.target.style.borderBottomColor = 'rgba(255,255,255,0.12)')}
                style={{
                  fontSize: 13, fontWeight: 500, border: 'none',
                  borderBottom: '1px solid rgba(255,255,255,0.12)',
                  outline: 'none', paddingBottom: 6, marginBottom: 10,
                  background: 'transparent', color: D.text1, width: '100%',
                }}
              />

              {[
                { key: 'bot_pct',    label: 'Bot',    colour: COLOURS.violet, max: 70 },
                { key: 'borrow_pct', label: 'Borrow', colour: COLOURS.gold,   max: 60 },
                { key: 'build_pct',  label: 'Build',  colour: COLOURS.teal,   max: 70 },
                { key: 'buy_pct',    label: 'Buy',    colour: COLOURS.gray,   max: 40 },
              ].map((row) => (
                <div key={row.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                    <span style={{ color: row.colour, fontWeight: 500 }}>
                      {row.label}
                    </span>
                    <span style={{ color: D.text1 }}>{s[row.key]}%</span>
                  </div>
                  <input type="range" min={0} max={row.max} step={5} value={s[row.key]}
                    onChange={(e) => updateSlider(i, row.key, parseInt(e.target.value, 10))}
                    className={`sw-slider ${SLIDER_CLASSES[row.key]}`}
                  />
                </div>
              ))}

              <div style={{ display: 'flex', height: 4, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: `${s.bot_pct}%`, background: COLOURS.violet }} />
                <div style={{ width: `${s.borrow_pct}%`, background: COLOURS.gold }} />
                <div style={{ width: `${s.build_pct}%`, background: COLOURS.teal }} />
                <div style={{ width: `${s.buy_pct}%`, background: COLOURS.gray }} />
              </div>

              <div style={{ fontSize: 11, color: total === 100 ? '#16a34a' : '#d97706' }}>
                {total === 100 ? 'Total: 100%' : `Total: ${total}% - adjust to 100%`}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: D.text2 }}>
                    Cost delta
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: netDelta >= 0 ? '#16a34a' : '#dc2626', marginTop: 2 }}>
                    {netDelta >= 0 ? '+' : ''}{formatCurrency(netDelta, currency)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: D.text2 }}>
                    Timeline
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2, color: D.text1 }}>{timeline} months</div>
                </div>
              </div>

              <div style={{ background: r.bg, color: r.text, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6, textAlign: 'center' }}>
                {r.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* AI Guidance panel */}
      {guidance && (
        <div style={{
          background: D.card, borderLeft: `3px solid ${COLOURS.gold}`,
          borderRadius: '0 12px 12px 0', padding: 20, marginBottom: 14,
          position: 'relative',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: COLOURS.teal, letterSpacing: '0.1em', textTransform: 'uppercase' }}>AI Guidance</span>
            <button onClick={() => setGuidance(null)} style={{ background: 'none', border: 'none', color: D.text2, fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>x</button>
          </div>
          <p style={{ fontSize: 15, color: D.text1, lineHeight: 1.7, margin: 0 }} dangerouslySetInnerHTML={{ __html: guidance.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
        </div>
      )}

      {/* SECTION 4 - comparison bar */}
      <div style={{ background: D.card, borderRadius: 12, border: `1px solid ${D.border}`, padding: '12px 14px', marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10, color: D.text1 }}>
          Cost delta - all three scenarios <InfoTooltip text="Visual comparison of the financial impact of all three scenarios for this function. The longest bar is the highest-saving option." />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {scenarios.map((s, i) => {
            const o = allOutputs[i];
            const widthPct = (Math.abs(o.netDelta) / maxAbsDelta) * 100;
            const dimmed = o.netDelta < 0;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 120, fontSize: 11, color: D.text2 }}>{s.name}</div>
                <div style={{ flex: 1, height: 14, background: 'rgba(255,255,255,0.04)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${widthPct}%`, height: '100%', background: scenarioColours[i], opacity: dimmed ? 0.4 : 1 }} />
                </div>
                <div style={{ width: 100, textAlign: 'right', fontSize: 11, fontWeight: 600, color: o.netDelta >= 0 ? '#16a34a' : '#dc2626' }}>
                  {o.netDelta >= 0 ? '+' : ''}{formatCurrency(o.netDelta, currency)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SECTION 5 - action bar */}
      <div style={{ borderTop: `1px solid ${D.border}`, padding: '10px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13 }}>
          {activeDoc.committed ? (
            <span style={{ color: COLOURS.teal, fontWeight: 500 }}>
              Scenario committed · {scenarios[activeDoc.committed_scenario_index]?.name}
            </span>
          ) : (
            <span style={{ color: D.text2 }}>Select a scenario to commit to blueprint</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <button onClick={handleGetGuidance} disabled={guidanceLoading}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: 500, borderRadius: 8,
              border: `1px solid ${COLOURS.teal}`, color: COLOURS.teal,
              background: 'transparent', cursor: guidanceLoading ? 'not-allowed' : 'pointer',
              opacity: guidanceLoading ? 0.6 : 1,
            }}>
            {guidanceLoading ? 'Getting guidance...' : 'Get guidance ↗'}
          </button>
          <div style={{ textAlign: 'center' }}>
            <button onClick={handleCommit} disabled={saving}
              style={{
                padding: '8px 16px', fontSize: 13, fontWeight: 500, borderRadius: 8,
                border: 'none', color: '#fff', background: COLOURS.teal,
                cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
              }}>
              {saving ? 'Saving…' : 'Commit to Blueprint'}
            </button>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
              Locks this scenario into your transformation blueprint for this function
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenarioWorkshop;
